/**
 * Задание API-ключа прямо в шапке.
 *
 * Ключ хранится только в localStorage браузера и уходит в бэкенд портала
 * заголовком `X-Api-Key`. Под ним выполняются все действия пользователя —
 * служебный токен портала на них не распространяется.
 */
import { useState } from "react";
import { getApiKey, setApiKey } from "../api/client";
import { useI18n } from "../i18n/I18nProvider";
import {
  AlertBanner,
  Badge,
  Button,
  ExternalIcon,
  FieldShell,
  Input,
  KeyIcon,
  Modal,
  typographyClass,
  useToast,
} from "../ui-kit";
import { useCatalog } from "./CatalogContext";
import { usePortalConfig } from "./usePortalConfig";

export function ApiKeyControl() {
  const { t } = useI18n();
  const toast = useToast();
  const { reload } = useCatalog();

  // Адрес страницы с ключами приходит из окружения бэкенда: фронт и основной
  // продукт живут на разных доменах, и хардкодить его в бандл нельзя.
  const apiKeysUrl = usePortalConfig()?.api_keys_url ?? null;

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(() => getApiKey() ?? "");
  const [stored, setStored] = useState(() => Boolean(getApiKey()));

  const close = () => {
    setOpen(false);
    setValue(getApiKey() ?? "");
  };

  const save = () => {
    const trimmed = value.trim();
    setApiKey(trimmed || null);
    setStored(Boolean(trimmed));
    toast.show({ title: trimmed ? t("auth.saved") : t("auth.cleared"), tone: "success" });
    setOpen(false);
    // каталог зависит от токена — перечитываем его под новым ключом
    reload();
  };

  const clear = () => {
    setApiKey(null);
    setValue("");
    setStored(false);
    toast.show({ title: t("auth.cleared") });
    setOpen(false);
    reload();
  };

  return (
    <>
      {/* На телефоне подпись уступает место ключу-иконке: статус ключа важнее
          слова «API-ключ», а вся шапка должна умещаться в 320px. Подменой
          занимается CSS — состояние у кнопки одно. */}
      <Button
        size="small"
        variant={stored ? "secondary" : "tertiary"}
        onClick={() => setOpen(true)}
        // aria-label подменяет всё доступное имя целиком, поэтому статус из
        // Badge пришлось бы иначе выбросить: скринридер объявлял бы «API-ключ»
        // и молчал о том, задан он или нет.
        aria-label={t("apiKey.buttonWithStatus", {
          status: stored ? t("apiKey.set") : t("apiKey.notSet"),
        })}
        startIcon={
          <span className="api-key-button__icon" aria-hidden="true">
            <KeyIcon />
          </span>
        }
        endIcon={
          <Badge tone={stored ? "positive" : "grey"} size="small">
            {stored ? t("apiKey.set") : t("apiKey.notSet")}
          </Badge>
        }
      >
        <span className="api-key-button__label">{t("apiKey.button")}</span>
      </Button>

      <Modal
        open={open}
        title={t("apiKey.title")}
        onClose={close}
        actions={
          <>
            {stored && (
              <Button variant="danger" onClick={clear}>
                {t("auth.clear")}
              </Button>
            )}
            <Button variant="ghost" onClick={close}>
              {t("action.close")}
            </Button>
            <Button onClick={save}>{t("action.save")}</Button>
          </>
        }
      >
        <p className={typographyClass("caption-13-medium")} style={{ color: "var(--color-text-secondary-v2)" }}>
          {t("auth.subtitle")}
        </p>

        <FieldShell label={t("auth.key")} htmlFor="api-key-field">
          <Input
            id="api-key-field"
            type="password"
            autoComplete="off"
            value={value}
            placeholder={t("auth.keyPlaceholder")}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") save();
            }}
          />
        </FieldShell>

        {apiKeysUrl && (
          <p className={`api-key-hint ${typographyClass("caption-13-medium")}`}>
            {t("auth.noKey")}{" "}
            <a href={apiKeysUrl} target="_blank" rel="noreferrer noopener">
              {t("auth.getKey")} <ExternalIcon />
            </a>
          </p>
        )}

        <AlertBanner tone="info">
          {stored ? t("auth.usingOwn") : t("auth.usingStatic")}
        </AlertBanner>
      </Modal>
    </>
  );
}
